import type { ICancellation } from "@/data/model/Cancellation";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
import { DeallocationReason } from "@/data/model/Enums";
import type {
    BillItemSelectionChangedEvent,
    IAllocationuseCaseChangedEvent,
    IBillChangedEvent,
    IBillClosedEvent,
    IBillDiscountChangedEvent,
    IBillManualEndUsersCountChangedEvent,
    IBillNameChangedEvent,
    IBillOpenedEvent,
    ICartChangedEvent,
    ICartDiscountChangedEvent,
    ICertificateEncodingProgressEvent,
    ICustomerFulfillmentChangedEvent,
    ICustomerLinkedFulfillmentsChangedEvent,
    ICustomerOfferNeedsRefreshEvent,
    IEmailConfirmedEvent,
    IFulfillmentPositionsChangedEvent,
    IIdentityDiscoveredEvent,
    IIdentityEmailChangedEvent,
    ILinkedFulfillmentPositionsChangedEvent,
    INotPaidOrderCreatedEvent,
    IOrderOutstandingChangedEvent,
    IRoomOfferActivationChangedEvent,
    IRoomPlanChangedEvent,
    ITableCovidUserCountChangedEvent,
    UserAllocatedEvent,
    UserDeallocatedEvent,
} from "@/data/model/Events";
import type { IOperatorFulfillment } from "@/data/model/Operator";
import { Signal, TypedSignal } from "@/data/util/Signal";

export const dataEvents = {
    // app socket events
    appSocket: {
        ItemSelectionChanged: new TypedSignal<BillItemSelectionChangedEvent>(),
        AllocationChange: new TypedSignal<DeallocationReason | undefined>(),
        UserAllocated: new TypedSignal<UserAllocatedEvent>(),
        UserDeallocated: new TypedSignal<UserDeallocatedEvent>(),
        AllocationUseCaseChange: new TypedSignal<IAllocationuseCaseChangedEvent>(),
        TableCovidUserCountChanged: new TypedSignal<ITableCovidUserCountChangedEvent>(),
        EmailConfirmed: new TypedSignal<IEmailConfirmedEvent>(),
        CartChanged: new TypedSignal<ICartChangedEvent>(),
        CartDiscountChanged: new TypedSignal<ICartDiscountChangedEvent>(),
        OfferChanged: new TypedSignal<ICustomerOfferNeedsRefreshEvent>(),
        PaymentConfirmed: new TypedSignal<IOrderPaymentConfirmation>(),
        FulfillmentChanged: new TypedSignal<ICustomerFulfillmentChangedEvent>(),
        LinkedFulfillmentsChanged: new TypedSignal<ICustomerLinkedFulfillmentsChangedEvent>(),
        FulfillmentPositionsChanged: new TypedSignal<IFulfillmentPositionsChangedEvent>(),
        LinkedFulfillmentsPositionsChanged: new TypedSignal<ILinkedFulfillmentPositionsChangedEvent>(),
        IdentityDiscovered: new TypedSignal<IIdentityDiscoveredEvent>(),
        IdentityEmailChanged: new TypedSignal<IIdentityEmailChangedEvent>(),
        CreditCardAttached: new TypedSignal<ICreditCard>(),
        CreditCardDetached: new TypedSignal<string>(),
        CertificateEncodingProgress: new TypedSignal<ICertificateEncodingProgressEvent>(),
        RoomOfferActivationChanged: new TypedSignal<IRoomOfferActivationChangedEvent>(),
        BillOpened: new TypedSignal<IBillOpenedEvent>(),
        BillChanged: new TypedSignal<IBillChangedEvent>(),
        BillDiscountChanged: new TypedSignal<IBillDiscountChangedEvent>(),
        BillNameChanged: new TypedSignal<IBillNameChangedEvent>(),
        BillManualEndUsersCountChanged: new TypedSignal<IBillManualEndUsersCountChangedEvent>(),
        BillClosed: new TypedSignal<IBillClosedEvent>(),
        UserSignedOut: new Signal(),
        UserSessionExpired: new Signal(),
        RoomPlanChanged: new TypedSignal<IRoomPlanChangedEvent>(),
    },
    // operator socket events
    operatorSocket: {
        TableCovidUserCountChanged: new TypedSignal<ITableCovidUserCountChangedEvent>(),
        RoomPlanChanged: new TypedSignal<IRoomPlanChangedEvent>(),
        OperatorUserSignedOut: new Signal(),
        OrderOutstandingChanged: new TypedSignal<IOrderOutstandingChangedEvent>(),
        NotPaidOrderCreated: new TypedSignal<INotPaidOrderCreatedEvent>(),
        FulfillmentChanged: new TypedSignal<IOperatorFulfillment>(),
        LinkedFulfillmentsChanged: new TypedSignal<IOperatorFulfillment[]>(),
        FulfillmentPositionsChanged: new TypedSignal<IFulfillmentPositionsChangedEvent>(),
        LinkedFulfillmentsPositionsChanged: new TypedSignal<ILinkedFulfillmentPositionsChangedEvent>(),
        OrderCancelled: new TypedSignal<ICancellation>(),
        BillOpened: new TypedSignal<IBillOpenedEvent>(),
        BillChanged: new TypedSignal<IBillChangedEvent>(),
        BillNameChanged: new TypedSignal<IBillNameChangedEvent>(),
        BillManualEndUsersCountChanged: new TypedSignal<IBillManualEndUsersCountChangedEvent>(),
        BillClosed: new TypedSignal<IBillClosedEvent>(),
        UserSessionExpired: new Signal(),
        BillDiscountChanged: new TypedSignal<IBillDiscountChangedEvent>(),
    },
};
